// @ts-nocheck
import { EventScheduleTabs } from '@/components/public/EventScheduleTabs';
import { useState, useEffect, useMemo } from "react";
import EventPromoVideo from '@/components/events/EventPromoVideo';
import SponsorLogo from "@/components/sponsors/SponsorLogo";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, MapPin, Calendar, Check, Ticket, Hotel, Tv, FileText, Camera, GraduationCap, ShoppingBag, CalendarClock } from "lucide-react";

// Strip HTML tags and truncate to N chars for tile preview
const previewText = (html: string | null | undefined, maxChars = 150): string => {
  if (!html) return "";
  let decoded = html;
  if (typeof document !== "undefined") {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    decoded = temp.textContent || temp.innerText || "";
  } else {
    decoded = html
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/&lt;/g, "<")
      .replace(/&gt;/g, ">")
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'");
  }
  const cleaned = decoded.replace(/\s+/g, " ").trim();
  if (cleaned.length <= maxChars) return cleaned;
  return cleaned.slice(0, maxChars).trim() + "…";
};
import { useAuth } from "@/contexts/AuthContext";
import { getRegistrationAvailability, isRegistrationBlocked } from "@/lib/registrationAvailability";
import MediaCredentialsForm from "@/components/events/MediaCredentialsForm";
import CoachAttendanceForm from "@/components/events/CoachAttendanceForm";

function fmtExtraPrice(p: any) {
  const n = Number(p) || 0;
  return `$${n.toLocaleString("en-US", { minimumFractionDigits: Number.isInteger(n) ? 0 : 2, maximumFractionDigits: 2 })}`;
}

export default function EventPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: event, isLoading, error } = useQuery({
    queryKey: ["public-event", slug],
    enabled: !!slug,
    queryFn: async () => {
      let q = await (supabase as any)
        .from("events")
        .select("*, sport:sports(id, name, default_cover_photo_url), event_type:event_types(id, name), main_venue:venues!main_venue_id(id, name, city, state, address)")
        .eq("slug", slug)
        .eq("status", "published")
        .is("deleted_at", null)
        .maybeSingle();
      if (q.data) return q.data;
      q = await (supabase as any)
        .from("events")
        .select("*, sport:sports(id, name, default_cover_photo_url), event_type:event_types(id, name), main_venue:venues!main_venue_id(id, name, city, state, address)")
        .eq("id", slug)
        .eq("status", "published")
        .is("deleted_at", null)
        .maybeSingle();
      if (q.error) throw q.error;
      return q.data;
    },
  });

  const eventId = event?.id;

  const { data: pricingDivisions = [] } = useQuery({
    queryKey: ["event-divisions-pricing", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      try {
        const { data } = await (supabase as any)
          .from("event_divisions")
          .select("id, name, age_group, gender, price_override, sort_order, is_active")
          .eq("event_id", eventId)
          .eq("is_active", true)
          .order("sort_order", { ascending: true })
          .order("name", { ascending: true });
        return data || [];
      } catch { return []; }
    },
  });

  const { data: ticketOptions = [] } = useQuery({
    queryKey: ["event-ticket-options-public", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      try {
        const { data } = await (supabase as any)
          .from("event_ticket_options")
          .select("id, name, price, description, sort_order, is_active")
          .eq("event_id", eventId)
          .eq("is_active", true)
          .order("sort_order", { ascending: true });
        return data || [];
      } catch { return []; }
    },
  });

  const { data: waiverSettings = null } = useQuery({
    queryKey: ["event-waiver-settings-public", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      try {
        const { data } = await (supabase as any)
          .from("event_waiver_settings")
          .select("team_waiver_required, individual_waiver_required")
          .eq("event_id", eventId)
          .maybeSingle();
        return data || null;
      } catch { return null; }
    },
  });


  const { data: extras = [] } = useQuery({
    queryKey: ["event-extras-public", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      try {
        const { data } = await (supabase as any)
          .from("event_extras")
          .select("id, name, description, price, thumbnail_url, level, is_required, sort_order, is_active")
          .eq("event_id", eventId)
          .eq("is_active", true)
          .order("sort_order", { ascending: true });
        return data || [];
      } catch { return []; }
    },
  });

  const { data: hotelProperties = [] } = useQuery({
    queryKey: ["event-hotel-properties-public", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      try {
        const { data } = await (supabase as any)
          .from("hotel_properties")
          .select("id, hotel_name, city, state, distance_miles, group_rate_per_night, rate_valid_until, booking_link, star_rating, amenities, photo_url, is_primary")
          .eq("event_id", eventId)
          .order("is_primary", { ascending: false })
          .order("distance_miles", { ascending: true, nullsFirst: false });
        return data || [];
      } catch { return []; }
    },
  });

  const { data: publicCoaches = [] } = useQuery({
    queryKey: ["event-coaches-public-tab", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      try {
        const { data } = await (supabase as any)
          .from("event_coaches")
          .select("id, coach_name, college_name, division, logo_url, display_order, hide_from_public")
          .eq("event_id", eventId)
          .or("hide_from_public.is.null,hide_from_public.eq.false")
          .order("display_order", { ascending: true, nullsFirst: false })
          .order("college_name", { ascending: true });
        return data || [];
      } catch { return []; }
    },
  });

  const { data: sponsors = [] } = useQuery({
    queryKey: ["event-sponsors-public", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("event_sponsors")
        .select("display_order, sponsor:sponsors(id,name,logo_url,website_url)")
        .eq("event_id", eventId)
        .order("display_order");
      return (data || []).filter((r: any) => r.sponsor);
    },
  });

  const venueIds = [event?.main_venue_id, ...(event?.additional_venue_ids || [])].filter(Boolean);
  const { data: allVenues = [] } = useQuery({
    queryKey: ["event-all-venues", eventId, venueIds.join(",")],
    enabled: !!eventId && venueIds.length > 0,
    queryFn: async () => {
      try {
        const { data } = await (supabase as any)
          .from("venues")
          .select("id, name, address, city, state, zip_code")
          .in("id", venueIds);
        if (!data) return [];
        // Preserve order: main first, then additional in original order
        const map = new Map(data.map((v: any) => [v.id, v]));
        return venueIds.map((id: string) => map.get(id)).filter(Boolean);
      } catch { return []; }
    },
  });

  const { data: venueInfo = [] } = useQuery({
    queryKey: ["event-venue-info-public", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      try {
        const { data } = await (supabase as any)
          .from("event_venue_info")
          .select("venue_id, divisions, doors_open, notes")
          .eq("event_id", eventId);
        return data || [];
      } catch { return []; }
    },
  });

  const { data: faqs = [] } = useQuery({
    queryKey: ["event-faqs-public", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("event_faq_links")
        .select("id, override_answer, display_order, faq:faq_catalog(question, answer, category)")
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("display_order");
      return (data || []).filter((r: any) => r.faq);
    },
  });

  const { data: banners = [] } = useQuery({
    queryKey: ["event-banners", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("ad_placements")
        .select(`
          id, placement_slot, sort_order,
          asset:ad_assets(id, name, advertiser_name, image_url, click_url, alt_text, start_date, end_date, is_active)
        `)
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("placement_slot", { ascending: true })
        .order("sort_order", { ascending: true });
      if (error) return [];
      const today = new Date().toISOString().slice(0, 10);
      return (data || []).filter((p: any) => {
        if (!p.asset || p.asset.is_active === false) return false;
        if (p.asset.start_date && p.asset.start_date > today) return false;
        if (p.asset.end_date && p.asset.end_date < today) return false;
        return true;
      });
    },
  });

  const bannersBySlot = useMemo(() => {
    const out: Record<string, any[]> = { event_page_top: [], event_page_pre_faq: [], event_page_sidebar: [] };
    for (const b of banners) {
      if (out[b.placement_slot]) out[b.placement_slot].push(b);
    }
    return out;
  }, [banners]);

  const [openModal, setOpenModal] = useState<
    null | "schedule" | "checkin" | "tickets" | "hotels" | "livestream" | "rules" | "media" | "coaches"
  >(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const handleAuthGatedPurchase = (
    type: "ticket" | "media" | "coach_registration",
    _refId: string | null
  ) => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(window.location.pathname)}`);
      return;
    }
    alert(`${type} purchase coming soon. Phase 4 will wire up Stripe checkout.`);
  };

  useEffect(() => {
    if (lightboxIndex === null) return;
    const imgs = Array.isArray(event?.gallery_images) ? event.gallery_images : [];
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIndex(null);
      if (e.key === "ArrowLeft") setLightboxIndex((lightboxIndex - 1 + imgs.length) % imgs.length);
      if (e.key === "ArrowRight") setLightboxIndex((lightboxIndex + 1) % imgs.length);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightboxIndex, event?.gallery_images]);

  const handleRegister = () => {
    if (!event) return;
    const a = getRegistrationAvailability(event);
    if (a.state === "external") {
      window.open(a.externalUrl, "_blank");
      return;
    }
    if (isRegistrationBlocked(a)) return;
    navigate(`/events/${slug}/register`);
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-semibold mb-2">Event not found</h1>
        <p className="text-muted-foreground mb-6">
          This event may not be published yet, or the link is incorrect.
        </p>
        <Button asChild>
          <Link to="/events">Browse events</Link>
        </Button>
      </div>
    );
  }


  const availability = getRegistrationAvailability(event);
  const regBlocked = isRegistrationBlocked(availability);
  const regButtonLabel = availability.label;

  const regInfo = (() => {
    if (regBlocked)
      return { value: "Closed", label: "Registration", color: "text-status-danger" };
    if (!event.registration_close_date)
      return { value: "Open", label: "Registration", color: "text-cyan" };
    const today = new Date(); today.setHours(0,0,0,0);
    const close = new Date(event.registration_close_date + "T00:00:00");
    const diff = Math.round((close.getTime() - today.getTime()) / 86400000);
    if (diff < 0) return { value: "Closed", label: "Registration", color: "text-status-danger" };
    if (diff === 0) return { value: "Today", label: "Last day", color: "text-status-warning" };
    return { value: String(diff), label: "Days to register", color: "text-cyan" };
  })();

  const PROSE_CLS = "prose prose-sm max-w-none [&_p]:text-body-md [&_p]:text-ink-secondary [&_p]:leading-relaxed [&_p]:mb-3 [&_strong]:text-ink-primary [&_strong]:font-semibold [&_u]:underline [&_a]:text-cyan [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-cyan-hover [&_h1]:text-heading-3 [&_h1]:text-ink-primary [&_h1]:mt-6 [&_h1]:mb-3 [&_h2]:text-heading-3 [&_h2]:text-ink-primary [&_h2]:mt-6 [&_h2]:mb-2 [&_h3]:text-heading-4 [&_h3]:text-ink-primary [&_h3]:mt-4 [&_h3]:mb-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3 [&_li]:text-body-md [&_li]:text-ink-secondary [&_li]:mb-1";

  const SectionCard = ({ title, children }: { title?: string; children: any }) => (
    <section className="bg-surface-card border border-stroke-subtle rounded-token-xl shadow-token-sm p-6 md:p-8">
      {title && <h2 className="text-heading-3 text-ink-primary mb-5">{title}</h2>}
      {children}
    </section>
  );

  const fmtPrice = (p: any) => {
    if (p == null || Number(p) <= 0) return "—";
    const n = Number(p);
    return "$" + (Number.isInteger(n) ? n.toString() : n.toFixed(2));
  };
  const cap = (s: any) => s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : "—";
  const fmtDateLong = (d: string) => {
    try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
    catch { return d; }
  };
  const fmtDateShort = (d: string) => {
    try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return d; }
  };
  const fmtDateRange = (s?: string, e?: string) => {
    if (!s) return "—";
    if (!e || s === e) return fmtDateLong(s);
    return `${fmtDateShort(s)} – ${fmtDateShort(e)}`;
  };

  const lowestPrice = (() => {
    const prices = pricingDivisions
      .map((d: any) => Number(d.price_override))
      .filter((n: number) => n > 0);
    return prices.length ? Math.min(...prices) : null;
  })();

  // Resolve hero image: cover_photo_url → sport default → gradient
  const heroImage =
    event.cover_photo_url?.trim?.() ||
    event.sport?.default_cover_photo_url?.trim?.() ||
    null;

  // Hero date range
  const heroDateRange = fmtDateRange(event.start_date, event.end_date);

  // Resolve venue: prefer joined main_venue, fall back to inline columns
  const venueName = event.main_venue?.name || event.venue_name || "";
  const venueCity = event.main_venue?.city || event.venue_city || "";
  const venueState = event.main_venue?.state || event.venue_state || "";

  const heroLocation = [venueName, venueCity, venueState]
    .filter(Boolean)
    .join(", ");

  // Category pills — sport, gender, event_type
  const heroCategories = [
    event.sport?.name,
    event.gender,
    event.event_type?.name,
  ].filter(Boolean);

  const hasPublishedSchedule = !!event.published_schedule_version_id;
  const hasExternalSchedule = !!event.external_schedule_url?.trim?.();
  // Only a published schedule or an external schedule link counts as "scheduled".
  // schedule_info (daily hours copy) alone must NOT enable the button.
  const hasSchedule = hasPublishedSchedule || hasExternalSchedule;
  const scheduleLabel = hasSchedule ? "View Schedule" : "Schedule Coming Soon";
  const handleViewSchedule = () => {
    if (hasPublishedSchedule) {
      navigate(`/schedule/${event.slug || slug}`);
      return;
    }
    if (hasSchedule) setOpenModal("schedule");
  };


  const galleryImages = Array.isArray(event.gallery_images) ? event.gallery_images : [];

  const shortDescription = (value: string | null | undefined) => previewText(value, 200);

  const tiles = ([
    {
      id: "checkin" as const,
      title: "Check-in",
      icon: <Check className="w-6 h-6 text-cyan" />,
      hasContent: !!event.checkin_short_description?.trim?.() || !!event.checkin_instructions?.trim?.(),
      preview: shortDescription(event.checkin_short_description),
      onClick: () => setOpenModal("checkin"),
    },
    {
      id: "tickets" as const,
      title: "Spectator Tickets",
      icon: <Ticket className="w-6 h-6 text-cyan" />,
      hasContent:
        !!event.tickets_short_description?.trim?.() ||
        !!event.ticket_display_text?.trim?.() ||
        !!event.external_tickets_url?.trim?.() ||
        ticketOptions.length > 0,
      preview: shortDescription(event.tickets_short_description),
      onClick: () => {
        if (event.use_external_tickets && event.external_tickets_url?.trim?.()) {
          window.open(event.external_tickets_url, "_blank", "noopener,noreferrer");
        } else {
          setOpenModal("tickets");
        }
      },
    },
    {
      id: "hotels" as const,
      title: "Hotels",
      icon: <Hotel className="w-6 h-6 text-cyan" />,
      hasContent:
        !!event.hotels_short_description?.trim?.() ||
        !!event.hotels_info?.trim?.() ||
        !!event.hotel_booking_url?.trim?.() ||
        !!event.stay_to_play_enabled ||
        hotelProperties.length > 0,
      preview: shortDescription(event.hotels_short_description),
      onClick: () => {
        if (event.hotel_booking_url?.trim?.()) {
          window.open(event.hotel_booking_url, "_blank", "noopener,noreferrer");
        } else {
          setOpenModal("hotels");
        }
      },
    },
    {
      id: "livestream" as const,
      title: "Live Stream",
      icon: <Tv className="w-6 h-6 text-cyan" />,
      hasContent: !!event.live_stream_short_description?.trim?.() || !!event.live_streaming_url?.trim?.() || !!event.live_streaming_description?.trim?.(),
      preview: shortDescription(event.live_stream_short_description),
      onClick: () => {
        if (event.live_streaming_url?.trim?.()) {
          window.open(event.live_streaming_url, "_blank", "noopener,noreferrer");
        } else {
          setOpenModal("livestream");
        }
      },
    },
    {
      id: "rules" as const,
      title: "Game Rules",
      icon: <FileText className="w-6 h-6 text-cyan" />,
      hasContent: !!event.rules_short_description?.trim?.() || !!event.rules?.trim?.(),
      preview: shortDescription(event.rules_short_description),
      onClick: () => setOpenModal("rules"),
    },
    {
      id: "media" as const,
      title: "Media Credentials",
      icon: <Camera className="w-6 h-6 text-cyan" />,
      hasContent:
        !!event.media_credentials_short_description?.trim?.() ||
        !!event.media_credentials_description?.trim?.() ||
        !!event.enable_media_credentials ||
        !!event.media_credentials_enabled,
      preview: shortDescription(event.media_credentials_short_description),
      onClick: () => setOpenModal("media"),
    },
    event.show_coaches_publicly !== false && {
      id: "coaches" as const,
      title: "College Coaches",
      icon: <GraduationCap className="w-6 h-6 text-cyan" />,
      hasContent: true,
      preview: shortDescription(event.coaches_short_description),
      onClick: () => setOpenModal("coaches"),
    },
    {
      id: "extras" as const,
      title: "Event Extras",
      icon: <ShoppingBag className="w-6 h-6 text-cyan" />,
      hasContent: true,
      preview: shortDescription(event.extras_short_description),
      onClick: () => window.open("/event-extras", "_blank"),
    },
    {
      id: "schedule_requests" as const,
      title: "Schedule Requests",
      icon: <CalendarClock className="w-6 h-6 text-cyan" />,
      hasContent: true,
      preview: shortDescription(event.schedule_requests_short_description),
      onClick: () => window.open("/schedule-requests", "_blank"),
    },
  ].filter(Boolean)) as any[];

  const modalTitleMap: Record<string, string> = {
    schedule: "Schedule",
    checkin: "Check-in",
    tickets: "Spectator Tickets",
    hotels: "Hotels",
    livestream: "Live Streaming",
    rules: "Game Rules",
    media: "Media Credentials",
    coaches: "College Coaches Attending",
  };

  return (
    <div className="bg-surface-page min-h-screen">

      {/* HERO BANNER */}
      <section className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
        {heroImage ? (
          <img src={heroImage} alt={event.name}
               className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-brand via-brand to-cyan" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="max-w-7xl mx-auto px-6 pb-8 md:pb-10">
            {/* Event title */}
            <h1 className="text-display-md md:text-display-lg text-white font-display leading-tight max-w-4xl">
              {event.name}
            </h1>

            {/* Date + Location meta line */}
            {(heroDateRange || heroLocation) && (
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-body-md text-white/85">
                {heroDateRange && (
                  <span className="inline-flex items-center gap-1.5">
                    <svg className="w-4 h-4 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {heroDateRange}
                  </span>
                )}
                {heroDateRange && heroLocation && (
                  <span className="text-white/40">·</span>
                )}
                {heroLocation && (
                  <span className="inline-flex items-center gap-1.5">
                    <svg className="w-4 h-4 opacity-80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {heroLocation}
                  </span>
                )}
              </div>
            )}

            {/* All badges combined: categories + Stay-to-Play + NCAA Live */}
            {(heroCategories.length > 0 || event.is_stay_to_play || event.stay_to_play_enabled || event.is_ncaa_live_event) && (
              <div className="flex flex-wrap gap-2 mt-4">
                {heroCategories.map((c, i) => (
                  <span
                    key={`cat-${i}`}
                    className="inline-flex items-center bg-white/10 backdrop-blur text-white/90 text-caption font-medium uppercase tracking-wider px-2.5 py-1 rounded-token-sm border border-white/15"
                  >
                    {c}
                  </span>
                ))}
                {(event.is_stay_to_play || event.stay_to_play_enabled) && (
                  <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur text-white text-caption font-medium uppercase tracking-wide px-3 py-1 rounded-token-md border border-white/20">
                    🏨 Stay-to-Play
                  </span>
                )}
                {event.is_ncaa_live_event && (
                  <span className="inline-flex items-center gap-1.5 bg-cyan text-cyan-foreground text-caption font-medium uppercase tracking-wide px-3 py-1 rounded-token-md">
                    NCAA Live Period
                  </span>
                )}
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleRegister}
                disabled={regBlocked}
                className="inline-flex items-center bg-cyan text-cyan-foreground hover:bg-cyan-hover disabled:opacity-50 disabled:cursor-not-allowed px-8 py-3 rounded-token-lg shadow-token-md font-medium text-body-lg transition-colors"
              >
                {regButtonLabel}
              </button>
              <button
                type="button"
                onClick={handleViewSchedule}
                disabled={!hasSchedule}
                className="inline-flex items-center bg-white/10 backdrop-blur text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed border border-white/30 px-8 py-3 rounded-token-lg font-medium text-body-lg transition-colors"
              >
                {scheduleLabel}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN GRID */}
      <div className="max-w-7xl mx-auto px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6 lg:gap-10">

          {/* SIDEBAR */}
          <aside className="lg:order-1 order-2">
            <div className="lg:sticky lg:top-6 space-y-4">

              {/* Register card */}
              <div className="bg-surface-raised border border-stroke-subtle rounded-token-xl shadow-token-md overflow-hidden">
                {event.thumbnail_url?.trim?.() && (
                  <div className="aspect-square w-full bg-surface-sunken">
                    <img
                      src={event.thumbnail_url}
                      alt={event.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                <div className="p-6">
                <dl className="space-y-3 mb-5">
                  <div>
                    <dt className="text-caption text-ink-tertiary uppercase tracking-wide">Dates</dt>
                    <dd className="text-body-md text-ink-primary font-medium mt-0.5">{fmtDateRange(event.start_date, event.end_date)}</dd>
                  </div>
                  {/* Venue moved to dedicated Venues sidebar card below */}
                  {event.registration_close_date && (
                    <div>
                      <dt className="text-caption text-ink-tertiary uppercase tracking-wide">Registration {regInfo.value === "Closed" ? "Status" : "Closes"}</dt>
                      <dd className={`text-body-md font-medium mt-0.5 ${regInfo.color}`}>
                        {regInfo.value === "Closed" ? "Closed" : fmtDateLong(event.registration_close_date)}
                      </dd>
                    </div>
                  )}
                  {event.roster_deadline && (
                    <div>
                      <dt className="text-caption text-ink-tertiary uppercase tracking-wide">Roster Deadline</dt>
                      <dd className="text-body-md text-ink-primary font-medium mt-0.5">{fmtDateLong(event.roster_deadline)}</dd>
                    </div>
                  )}
                </dl>

                <button
                  type="button"
                  onClick={handleRegister}
                  disabled={regBlocked}
                  className="w-full inline-flex items-center justify-center bg-cyan text-cyan-foreground hover:bg-cyan-hover disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3 rounded-token-lg shadow-token-sm font-medium text-body-lg transition-colors"
                >
                  {regButtonLabel}
                </button>
                {availability.message && (
                  <p className="text-caption text-ink-tertiary text-center mt-2">
                    {availability.message}
                  </p>
                )}
                {lowestPrice !== null && regInfo.value !== "Closed" && (
                  <div className="text-caption text-ink-tertiary text-center mt-2">
                    Starting from <span className="font-mono tabular-nums text-ink-secondary">${lowestPrice}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleViewSchedule}
                  disabled={!hasSchedule}
                  className="w-full mt-3 inline-flex items-center justify-center bg-surface-page border border-stroke-subtle text-ink-primary hover:border-cyan hover:text-cyan disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-stroke-subtle disabled:hover:text-ink-primary px-6 py-2.5 rounded-token-lg font-medium text-body-md transition-colors"
                >
                  {scheduleLabel}
                </button>
                </div>
              </div>

              {/* Venues card */}
              {(allVenues.length > 0 || venueName) && (
                <div className="bg-surface-raised border border-stroke-subtle rounded-token-xl shadow-token-sm p-6">
                  <h3 className="text-heading-4 text-ink-primary mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-cyan" />
                    Venues
                  </h3>
                  <ul className="space-y-4">
                    {(allVenues.length > 0 ? allVenues : [{
                      id: "fallback",
                      name: venueName,
                      address: event.venue_address,
                      city: venueCity,
                      state: venueState,
                      zip_code: event.venue_zip,
                    }]).map((v: any, idx: number) => {
                      const cityLine = [v.city, v.state].filter(Boolean).join(", ");
                      const fullAddr = [v.address, cityLine, v.zip_code].filter(Boolean).join(", ");
                      const mapsUrl = fullAddr ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddr)}` : null;
                      return (
                        <li key={v.id || idx} className="pb-4 border-b border-stroke-subtle last:border-b-0 last:pb-0">
                          <div className="text-body-md text-ink-primary font-medium">{v.name}</div>
                          {v.address && (
                            <div className="text-body-sm text-ink-secondary mt-0.5">{v.address}</div>
                          )}
                          {cityLine && (
                            <div className="text-body-sm text-ink-secondary">
                              {cityLine}{v.zip_code ? `, ${v.zip_code}` : ""}
                            </div>
                          )}
                          {(() => {
                            const info: any = venueInfo.find((r: any) => r.venue_id === v.id);
                            if (!info) return null;
                            return (
                              <>
                                {info.divisions?.trim() && (
                                  <div className="text-body-sm text-ink-primary mt-1">Age Divisions: {info.divisions}</div>
                                )}
                                {info.doors_open?.trim() && (
                                  <div className="text-body-sm text-ink-primary">Doors Open: {info.doors_open}</div>
                                )}
                                {info.notes?.trim() && (
                                  <div className="text-body-sm text-ink-secondary italic">{info.notes}</div>
                                )}
                              </>
                            );
                          })()}
                          {mapsUrl && (
                            <a href={mapsUrl} target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center gap-1 text-caption text-cyan hover:text-cyan-hover mt-1.5">
                              Get directions →
                            </a>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Divisions card */}
              {(event.age_groups_description?.trim?.() || pricingDivisions.length > 0) && (
                <div className="bg-surface-raised border border-stroke-subtle rounded-token-xl shadow-token-sm p-6">
                  <h3 className="text-heading-4 text-ink-primary mb-4">Divisions</h3>
                  {event.age_groups_description?.trim?.() && (
                    <div className="prose prose-sm max-w-none [&_p]:text-body-sm [&_p]:text-ink-secondary [&_p]:leading-relaxed [&_p]:mb-2 [&_strong]:text-ink-primary [&_a]:text-cyan [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:text-body-sm [&_li]:text-ink-secondary [&_li]:mb-1 mb-4"
                         dangerouslySetInnerHTML={{ __html: event.age_groups_description }} />
                  )}
                  {pricingDivisions.length > 0 && (
                    <ul className="space-y-2.5">
                      {pricingDivisions.map((d: any) => (
                        <li key={d.id} className="flex items-start justify-between gap-3 pb-2.5 border-b border-stroke-subtle last:border-b-0 last:pb-0">
                          <div className="min-w-0 flex-1">
                            <div className="text-body-sm text-ink-primary font-medium">{d.name}</div>
                            {(d.age_group || d.gender) && (
                              <div className="text-caption text-ink-tertiary mt-0.5">
                                {[d.age_group, d.gender ? cap(d.gender) : null].filter(Boolean).join(" · ")}
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Pricing card */}
              {(event.pricing_notes?.trim?.() || pricingDivisions.length > 0) && (
                <div className="bg-surface-raised border border-stroke-subtle rounded-token-xl shadow-token-sm p-6">
                  <h3 className="text-heading-4 text-ink-primary mb-4">Pricing</h3>
                  {event.pricing_notes?.trim?.() && (
                    <div className="prose prose-sm max-w-none [&_p]:text-body-sm [&_p]:text-ink-secondary [&_p]:leading-relaxed [&_p]:mb-2 [&_strong]:text-ink-primary [&_a]:text-cyan [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_li]:text-body-sm [&_li]:text-ink-secondary [&_li]:mb-1 mb-4"
                         dangerouslySetInnerHTML={{ __html: event.pricing_notes }} />
                  )}
                  {pricingDivisions.length > 0 && (
                    <>
                      <div className="text-caption text-ink-tertiary uppercase tracking-wide mb-2 mt-2">Division Pricing</div>
                      <ul className="space-y-2.5">
                        {pricingDivisions.map((d: any) => (
                          <li key={d.id} className="flex items-start justify-between gap-3 pb-2.5 border-b border-stroke-subtle last:border-b-0 last:pb-0">
                            <div className="min-w-0 flex-1">
                              <div className="text-body-sm text-ink-primary font-medium">{d.name}</div>
                              {(d.age_group || d.gender) && (
                                <div className="text-caption text-ink-tertiary mt-0.5">
                                  {[d.age_group, d.gender ? cap(d.gender) : null].filter(Boolean).join(" · ")}
                                </div>
                              )}
                            </div>
                            <div className="text-body-sm font-mono tabular-nums text-cyan font-medium whitespace-nowrap">
                              {fmtPrice(d.price_override)}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}


              {/* Add-ons card */}
              {extras.length > 0 && (
                <div className="bg-surface-raised border border-stroke-subtle rounded-token-xl shadow-token-sm p-6">
                  <h3 className="text-heading-4 text-ink-primary mb-4">Add-ons</h3>
                  <ul className="space-y-2.5">
                    {extras.map((x: any) => (
                      <li key={x.id} className="flex items-start justify-between gap-3 pb-2.5 border-b border-stroke-subtle last:border-b-0 last:pb-0">
                        <div className="min-w-0 flex-1">
                          <div className="text-body-sm text-ink-primary font-medium">
                            {x.name}
                            {x.is_required && (
                              <span className="inline-block ml-1.5 px-1.5 py-0.5 text-caption bg-status-warning-subtle text-status-warning rounded-token-sm">Required</span>
                            )}
                          </div>
                          {x.description && <div className="text-caption text-ink-tertiary mt-0.5">{x.description}</div>}
                        </div>
                        <div className="text-body-sm font-mono tabular-nums text-cyan font-medium whitespace-nowrap">
                          {fmtPrice(x.price)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Social Share card */}
              <div className="bg-surface-raised border border-stroke-subtle rounded-token-xl shadow-token-sm p-6">
                <h3 className="text-heading-4 text-ink-primary mb-4">Share This Event</h3>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-surface-page border border-stroke-subtle hover:border-cyan rounded-token-md px-3 py-2.5 text-body-sm text-ink-primary hover:text-cyan transition-colors"
                  >
                    Facebook
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "")}&text=${encodeURIComponent(event.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-surface-page border border-stroke-subtle hover:border-cyan rounded-token-md px-3 py-2.5 text-body-sm text-ink-primary hover:text-cyan transition-colors"
                  >
                    X / Twitter
                  </a>
                  <a
                    href={`mailto:?subject=${encodeURIComponent(event.name)}&body=${encodeURIComponent("Check out this event: " + (typeof window !== "undefined" ? window.location.href : ""))}`}
                    className="inline-flex items-center justify-center gap-2 bg-surface-page border border-stroke-subtle hover:border-cyan rounded-token-md px-3 py-2.5 text-body-sm text-ink-primary hover:text-cyan transition-colors"
                  >
                    Email
                  </a>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(window.location.href);
                      } catch {}
                    }}
                    className="inline-flex items-center justify-center gap-2 bg-surface-page border border-stroke-subtle hover:border-cyan rounded-token-md px-3 py-2.5 text-body-sm text-ink-primary hover:text-cyan transition-colors"
                  >
                    Copy Link
                  </button>
                </div>
                {typeof navigator !== "undefined" && "share" in navigator && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await (navigator as any).share({
                          title: event.name,
                          text: `Check out ${event.name}`,
                          url: window.location.href,
                        });
                      } catch {}
                    }}
                    className="md:hidden w-full inline-flex items-center justify-center bg-cyan text-cyan-foreground hover:bg-cyan-hover px-4 py-2.5 rounded-token-md text-body-sm font-medium transition-colors"
                  >
                    Share via...
                  </button>
                )}
              </div>

              {/* Sidebar banner */}
              <BannerSlot
                banners={bannersBySlot.event_page_sidebar}
                eventId={eventId}
                slot="event_page_sidebar"
                aspectRatio="aspect-[1/1]"
              />

            </div>
          </aside>

          {/* MAIN CONTENT */}
          <main className="lg:order-2 order-1 space-y-6">

            {/* About */}
            {event.description?.trim?.() && (
              <SectionCard title="About This Event">
                <div className={PROSE_CLS} dangerouslySetInnerHTML={{ __html: event.description }} />
              </SectionCard>
            )}

            {/* Top banner */}
            <BannerSlot
              banners={bannersBySlot.event_page_top}
              eventId={eventId}
              slot="event_page_top"
              aspectRatio="aspect-[5/1]"
            />

            {/* Promo video */}
            {event.promo_video_url?.trim?.() && (
              <SectionCard title="Event Video">
                <EventPromoVideo url={event.promo_video_url} className="rounded-token-lg" />
              </SectionCard>
            )}

            {/* Sponsors (under video) */}
            {sponsors.length > 0 && (
              <section className="bg-surface-card border border-stroke-subtle rounded-token-xl shadow-token-sm px-4 py-3 md:px-6 md:py-4">
                <h3 className="text-caption text-ink-tertiary uppercase tracking-wide mb-3">Sponsors</h3>
                <div className="flex flex-nowrap gap-x-10 items-center overflow-x-auto whitespace-nowrap [scrollbar-width:thin]">
                  {sponsors.map((row: any) => (
                    <SponsorLogo
                      key={row.sponsor.id}
                      sponsor={row.sponsor}
                      placement="event_page"
                      eventId={eventId}
                      className="shrink-0"
                      imgClassName="h-32 w-auto object-contain"
                    />
                  ))}
                </div>
              </section>
            )}


            {/* 2×4 Tile grid */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tiles.map((tile: any) => {
                const disabled = !tile.hasContent;
                return (
                  <button
                    key={tile.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => !disabled && tile.onClick()}
                    className={`group bg-surface-card border border-stroke-subtle rounded-token-xl shadow-token-sm p-6 text-left transition-all ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-cyan hover:shadow-token-md cursor-pointer"}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-12 h-12 rounded-token-md bg-cyan-subtle flex items-center justify-center">
                        {tile.icon}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <h3 className="text-heading-4 text-ink-primary">{tile.title}</h3>
                          {!disabled && (
                            <span className="text-ink-tertiary group-hover:text-cyan transition-colors text-body-md shrink-0">→</span>
                          )}
                        </div>
                        {tile.preview ? (
                          <p className="text-body-sm text-ink-secondary line-clamp-3">{tile.preview}</p>
                        ) : disabled ? (
                          <p className="text-caption text-ink-tertiary">Coming soon</p>
                        ) : null}
                      </div>
                    </div>
                  </button>
                );
              })}
            </section>

            {/* Gallery */}
            {galleryImages.length > 0 && (
              <SectionCard title="Event Gallery">
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {galleryImages.map((src: string, i: number) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setLightboxIndex(i)}
                      className="aspect-square rounded-token-md overflow-hidden bg-surface-sunken hover:opacity-80 transition-opacity"
                    >
                      <img
                        src={src}
                        alt={`${event.name} — image ${i + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e: any) => { e.currentTarget.src = "/placeholder.svg"; }}
                      />
                    </button>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* College Coaches Attending (public) */}
            {event.show_coaches_publicly !== false && publicCoaches.length > 0 && (
              <SectionCard title="College Coaches Attending">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {publicCoaches.map((c: any) => (
                    <div key={c.id} className="bg-surface-page border border-stroke-subtle rounded-token-lg p-3 flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-surface-sunken overflow-hidden shrink-0 flex items-center justify-center">
                        {c.logo_url ? (
                          <img src={c.logo_url} alt={c.college_name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-body-md text-ink-tertiary font-medium">{c.college_name?.[0] || "?"}</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-body-sm text-ink-primary font-medium truncate">{c.college_name}</div>
                        {c.coach_name && <div className="text-caption text-ink-secondary truncate">{c.coach_name}</div>}
                        {c.division && <div className="text-caption text-ink-tertiary">{c.division}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Pre-FAQ banner */}
            <BannerSlot
              banners={bannersBySlot.event_page_pre_faq}
              eventId={eventId}
              slot="event_page_pre_faq"
              aspectRatio="aspect-[5/1]"
            />

            {/* FAQs */}
            {faqs.length > 0 && (
              <SectionCard title="Frequently Asked Questions">
                <FAQsTab faqs={faqs} />
              </SectionCard>
            )}

            {/* Waiver — "required" comes from the event's waiver settings, not the legacy event flag */}
            {(() => {
              const waiverRequired =
                !!(waiverSettings as any)?.team_waiver_required ||
                !!(waiverSettings as any)?.individual_waiver_required;
              return (event.waiver_text?.trim?.() || event.waiver_url?.trim?.() || waiverRequired) ? (
              <SectionCard title="Waiver">
                {waiverRequired && (
                  <div className="bg-status-warning-subtle border border-status-warning rounded-token-lg p-4 mb-5 flex gap-3">
                    <div className="text-status-warning text-body-md font-medium shrink-0">⚠</div>
                    <div className="text-body-md text-ink-primary">All participants must agree to the waiver during registration.</div>
                  </div>
                )}
                {event.waiver_text?.trim?.() && (
                  <div className="bg-surface-page border border-stroke-subtle rounded-token-lg p-5 max-h-[500px] overflow-y-auto mb-4">
                    <div className={PROSE_CLS} dangerouslySetInnerHTML={{ __html: event.waiver_text }} />
                  </div>
                )}
                {event.waiver_url?.trim?.() && (
                  <a href={event.waiver_url} target="_blank" rel="noopener noreferrer"
                     className="inline-flex items-center gap-2 bg-surface-page border border-stroke-subtle rounded-token-lg px-4 py-2.5 text-body-md text-ink-primary hover:border-stroke-strong transition-colors">
                    View full waiver document →
                  </a>
                )}
              </SectionCard>
              ) : null;
            })()}

            {/* More Info */}
            {event.more_info?.trim?.() && (
              <SectionCard title="Additional Information">
                <div className={PROSE_CLS} dangerouslySetInnerHTML={{ __html: event.more_info }} />
              </SectionCard>
            )}

          </main>

        </div>

        {/* Event Extras */}
        {extras.length > 0 && (
          <section className="mt-10">
            <h2 className="text-heading-2 text-ink-primary font-display mb-4">Event Extras</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {extras.map((x: any) => (
                <Card key={x.id} className="hover:shadow-lg transition-shadow overflow-hidden flex flex-col">
                  <div className="h-48 overflow-hidden bg-muted">
                    <img
                      src={x.thumbnail_url || "/placeholder.svg"}
                      alt={x.name}
                      className="w-full h-full object-cover"
                      onError={(e: any) => { e.currentTarget.src = "/placeholder.svg"; }}
                    />
                  </div>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <CardTitle className="text-xl font-semibold">{x.name}</CardTitle>
                      <Badge variant="secondary" className="shrink-0">{x.level === "player" ? "Player" : "Team"}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-1 gap-3">
                    {x.description && <p className="text-sm text-muted-foreground">{x.description}</p>}
                    <div className="mt-auto flex items-center justify-between gap-3">
                      <span className="text-lg font-semibold tabular-nums">{fmtExtraPrice(x.price)}</span>
                      <Button asChild className="min-h-11">
                        <Link to={`/event-extras?event=${encodeURIComponent(event.id)}&extra=${encodeURIComponent(x.id)}`}>Purchase</Link>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Tile modals */}
      {openModal && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
          onClick={() => setOpenModal(null)}
        >
          <div
            className="bg-surface-card w-full md:max-w-2xl max-h-[90vh] overflow-y-auto rounded-t-token-xl md:rounded-token-xl shadow-token-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-surface-card border-b border-stroke-subtle px-6 py-4 flex items-center justify-between">
              <h2 className="text-heading-3 text-ink-primary">
                {modalTitleMap[openModal]}
              </h2>
              <button
                type="button"
                onClick={() => setOpenModal(null)}
                className="text-ink-tertiary hover:text-ink-primary transition-colors text-xl leading-none p-1"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="px-6 py-5">
              {openModal === "schedule" && (
                <>
                  <EventScheduleTabs eventId={event.id} />
                  {event.external_schedule_url?.trim?.() && (
                    <a href={event.external_schedule_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-2 mt-4 bg-cyan text-cyan-foreground hover:bg-cyan-hover rounded-token-lg px-4 py-2.5 text-body-md font-medium transition-colors">
                      View full schedule →
                    </a>
                  )}
                </>
              )}

              {openModal === "checkin" && event.checkin_instructions?.trim?.() && (
                <div className={PROSE_CLS} dangerouslySetInnerHTML={{ __html: event.checkin_instructions }} />
              )}

              {openModal === "hotels" && (
                <>
                  {event.hotels_info?.trim?.() && (
                    <div className={PROSE_CLS} dangerouslySetInnerHTML={{ __html: event.hotels_info }} />
                  )}
                  {event.stay_to_play_enabled && (
                    <div className="bg-status-info-subtle border border-status-info rounded-token-lg p-4 my-5 flex gap-3">
                      <div className="text-status-info text-body-md font-medium shrink-0">ℹ</div>
                      <div className="flex-1">
                        <div className="text-body-md text-ink-primary font-medium">
                          {event.stay_to_play_required ? "Stay-to-Play Required" : "Stay-to-Play Available"}
                        </div>
                        {event.stay_to_play_notes?.trim?.() && (
                          <div className="text-body-sm text-ink-secondary mt-1 prose prose-sm max-w-none [&_p]:mb-2 [&_a]:text-cyan [&_a]:underline"
                               dangerouslySetInnerHTML={{ __html: event.stay_to_play_notes }} />
                        )}
                      </div>
                    </div>
                  )}
                  {event.hotel_booking_url?.trim?.() && (
                    <a href={event.hotel_booking_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-2 bg-cyan text-cyan-foreground hover:bg-cyan-hover rounded-token-lg px-5 py-3 text-body-md font-medium shadow-token-sm transition-colors mb-6">
                      Book your hotel →
                    </a>
                  )}
                  {hotelProperties.length > 0 && (
                    <>
                      <h4 className="text-heading-4 text-ink-primary mb-3 mt-2">Partner Hotels</h4>
                      <div className="grid grid-cols-1 gap-4">
                        {hotelProperties.map((h: any) => (
                          <div key={h.id} className="bg-surface-page border border-stroke-subtle rounded-token-lg overflow-hidden">
                            {h.photo_url && (
                              <div className="aspect-[16/9] bg-surface-sunken overflow-hidden">
                                <img src={h.photo_url} alt={h.hotel_name} className="w-full h-full object-cover" />
                              </div>
                            )}
                            <div className="p-4">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <div className="text-body-md text-ink-primary font-medium">{h.hotel_name}</div>
                                  {(h.city || h.state) && (
                                    <div className="text-body-sm text-ink-tertiary mt-0.5">
                                      {[h.city, h.state].filter(Boolean).join(", ")}
                                      {h.distance_miles != null && ` · ${h.distance_miles} mi`}
                                    </div>
                                  )}
                                </div>
                                {h.is_primary && (
                                  <span className="px-2 py-0.5 text-caption bg-cyan-subtle text-cyan rounded-token-sm whitespace-nowrap">Recommended</span>
                                )}
                              </div>
                              {h.group_rate_per_night != null && (
                                <div className="mt-3">
                                  <span className="text-body-md font-mono tabular-nums text-cyan font-medium">${h.group_rate_per_night}</span>
                                  <span className="text-body-sm text-ink-tertiary ml-1">/ night</span>
                                </div>
                              )}
                              {h.booking_link && (
                                <a href={h.booking_link} target="_blank" rel="noopener noreferrer"
                                   className="inline-flex items-center text-body-sm text-cyan hover:text-cyan-hover mt-3 font-medium">
                                  Book →
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {openModal === "livestream" && (
                <>
                  {event.live_streaming_description?.trim?.() && (
                    <div className={PROSE_CLS} dangerouslySetInnerHTML={{ __html: event.live_streaming_description }} />
                  )}
                  {event.live_streaming_url?.trim?.() && (
                    <a href={event.live_streaming_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-2 mt-4 bg-cyan text-cyan-foreground hover:bg-cyan-hover rounded-token-lg px-5 py-3 text-body-md font-medium transition-colors">
                      Watch Now →
                    </a>
                  )}
                </>
              )}

              {openModal === "tickets" && (
                <>
                  {event.ticket_display_text?.trim?.() && (
                    <div className={PROSE_CLS} dangerouslySetInnerHTML={{ __html: event.ticket_display_text }} />
                  )}
                  {ticketOptions.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-heading-4 text-ink-primary mb-3">Available Tickets</h4>
                      <div className="space-y-3">
                        {ticketOptions.map((t: any) => (
                          <div key={t.id} className="bg-surface-page border border-stroke-subtle rounded-token-lg p-4 flex items-start justify-between gap-4">
                            <div>
                              <div className="text-body-md text-ink-primary font-medium">{t.name}</div>
                              {t.description && <div className="text-body-sm text-ink-tertiary mt-1">{t.description}</div>}
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-body-lg font-mono tabular-nums text-cyan font-medium whitespace-nowrap">
                                {fmtPrice(t.price)}
                              </div>
                              <button
                                type="button"
                                onClick={() => handleAuthGatedPurchase("ticket", t.id)}
                                className="bg-cyan text-cyan-foreground hover:bg-cyan-hover px-4 py-2 rounded-token-md text-body-sm font-medium transition-colors"
                              >
                                Buy
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {!event.ticket_display_text?.trim?.() && ticketOptions.length === 0 && (
                    <p className="text-body-md text-ink-tertiary">Ticket information coming soon.</p>
                  )}
                </>
              )}

              {openModal === "rules" && (
                event.rules?.trim?.() ? (
                  <div className={PROSE_CLS} dangerouslySetInnerHTML={{ __html: event.rules }} />
                ) : (
                  <p className="text-body-md text-ink-tertiary">Rules will be posted soon.</p>
                )
              )}

              {openModal === "media" && (
                <>
                  {event.media_credentials_description?.trim?.() && (
                    <div
                      className={PROSE_CLS}
                      onClickCapture={(e) => {
                        const a = (e.target as HTMLElement)?.closest?.("a");
                        if (a) {
                          a.setAttribute("target", "_blank");
                          a.setAttribute("rel", "noopener noreferrer");
                        }
                      }}
                      dangerouslySetInnerHTML={{ __html: event.media_credentials_description }}
                    />
                  )}
                  <div className="mt-6">
                    <MediaCredentialsForm
                      mediaCredentialsFormId={event.media_credentials_form_id}
                      eventId={event.id}
                      eventName={event.name}
                      notificationEmail={event.media_credentials_notification_email}
                      mediaCredentialsPrice={event.media_credentials_price || 0}
                      onSuccess={() => setOpenModal(null)}
                    />
                  </div>
                </>
              )}


              {openModal === "coaches" && (
                <div className="bg-cyan-subtle border border-cyan rounded-token-lg p-5">
                  <h4 className="text-heading-4 text-ink-primary mb-2">Are you a college coach?</h4>
                  <p className="text-body-sm text-ink-secondary mb-4">
                    Register to attend this event. Includes coach pass, hospitality room access, and player packets.
                  </p>
                  <CoachAttendanceForm
                    eventId={event.id}
                    eventName={event.name}
                    attendanceFee={Number((event as any).coach_attendance_fee) || 0}
                    couponCode={(event as any).coach_coupon_code || undefined}
                    instructions={(event as any).coach_registration_instructions || undefined}
                    onSuccess={() => setOpenModal(null)}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Lightbox overlay */}
      {lightboxIndex !== null && galleryImages[lightboxIndex] && (
        <div
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex(null); }}
            className="absolute top-4 right-4 text-white text-3xl hover:text-cyan transition-colors p-2"
            aria-label="Close"
          >
            ✕
          </button>
          <div className="absolute top-6 left-6 text-white/70 text-body-sm">
            {lightboxIndex + 1} / {galleryImages.length}
          </div>
          {galleryImages.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((lightboxIndex - 1 + galleryImages.length) % galleryImages.length);
              }}
              className="absolute left-4 md:left-8 text-white text-4xl hover:text-cyan transition-colors p-2"
              aria-label="Previous"
            >
              ‹
            </button>
          )}
          <img
            src={galleryImages[lightboxIndex]}
            alt={`${event.name} — image ${lightboxIndex + 1}`}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {galleryImages.length > 1 && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((lightboxIndex + 1) % galleryImages.length);
              }}
              className="absolute right-4 md:right-8 text-white text-4xl hover:text-cyan transition-colors p-2"
              aria-label="Next"
            >
              ›
            </button>
          )}
        </div>
      )}

      {/* Mobile sticky register CTA */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 bg-surface-raised border-t border-stroke-subtle p-3 z-40 shadow-token-lg">
        <button type="button" onClick={handleRegister}
                disabled={regBlocked}
                className="w-full inline-flex items-center justify-center bg-cyan text-cyan-foreground hover:bg-cyan-hover disabled:opacity-50 px-6 py-3 rounded-token-lg font-medium text-body-lg transition-colors">
          {regBlocked || availability.state === "external"
            ? regButtonLabel
            : `Register Team${lowestPrice !== null ? ` · From $${lowestPrice}` : ""}`}
        </button>
      </div>

      {/* Mobile bottom padding to clear sticky CTA */}
      <div className="lg:hidden h-20" />

    </div>
  );
}

function FAQsTab({ faqs }: { faqs: any[] }) {
  const [openId, setOpenId] = useState<string | null>(null);
  if (!faqs || faqs.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-body-md text-ink-tertiary">No FAQs available yet.</p>
      </div>
    );
  }
  return (
    <div className="max-w-3xl">
      {faqs.map((row: any) => {
        const id = row.id;
        const question = row.faq?.question || "";
        const answer = (row.override_answer && row.override_answer.trim()) || row.faq?.answer || "";
        const isOpen = openId === id;
        return (
          <div key={id} className="border-b border-stroke-subtle last:border-b-0">
            <button
              type="button"
              onClick={() => setOpenId(isOpen ? null : id)}
              className="w-full flex items-center justify-between text-left py-4 hover:bg-surface-sunken/50 transition-colors px-2 -mx-2 rounded-token-md"
            >
              <span className="text-body-md text-ink-primary font-medium pr-4">{question}</span>
              <span className={`text-ink-tertiary transition-transform ${isOpen ? "rotate-180" : ""}`}>▾</span>
            </button>
            {isOpen && (
              <div
                className="pb-4 px-2 prose prose-sm max-w-none [&_p]:text-body-md [&_p]:text-ink-secondary [&_p]:leading-relaxed [&_p]:mb-3 [&_a]:text-cyan [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_li]:text-body-md [&_li]:text-ink-secondary"
                dangerouslySetInnerHTML={{ __html: answer }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function getAdSessionId() {
  if (typeof window === "undefined") return "";
  try {
    let sid = sessionStorage.getItem("rl_ad_session_id");
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem("rl_ad_session_id", sid);
    }
    return sid;
  } catch {
    return "";
  }
}

function trackAdEvent(payload: {
  event_type: "impression" | "click";
  asset_id: string;
  placement_slot: string;
  event_id?: string;
  target_url?: string;
}) {
  if (typeof window === "undefined") return;
  const session_id = getAdSessionId();
  const url = `${(supabase as any).supabaseUrl}/functions/v1/track-ad-event`;
  const anonKey = (supabase as any).supabaseKey;
  try {
    const body = JSON.stringify({ ...payload, session_id });
    // fetch with keepalive survives navigation like sendBeacon, AND lets us
    // set the apikey header that the Supabase gateway requires.
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": anonKey,
        "Authorization": `Bearer ${anonKey}`,
      },
      body,
      keepalive: true,
      mode: "cors",
    }).catch(() => {});
  } catch {}
}

async function trackAdEventAsync(payload: {
  event_type: "impression" | "click";
  asset_id: string;
  placement_slot: string;
  event_id?: string;
  target_url?: string;
}): Promise<boolean> {
  if (typeof window === "undefined") return false;
  const session_id = getAdSessionId();
  const url = `${(supabase as any).supabaseUrl}/functions/v1/track-ad-event`;
  const anonKey = (supabase as any).supabaseKey;

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apikey": anonKey,
        "Authorization": `Bearer ${anonKey}`,
      },
      body: JSON.stringify({ ...payload, session_id }),
      mode: "cors",
    });

    if (!res.ok) {
      console.warn("[trackAdEvent] non-OK response:", res.status, await res.text().catch(() => ""));
      return false;
    }
    const data = await res.json().catch(() => ({}));
    if (data.ok === false) {
      console.warn("[trackAdEvent] function returned error:", data);
      return false;
    }
    return true;
  } catch (err) {
    console.warn("[trackAdEvent] fetch failed:", err);
    return false;
  }
}

function BannerSlot({
  banners,
  eventId,
  slot,
  className = "",
  aspectRatio = "aspect-[5/1]",
}: {
  banners: any[];
  eventId: string;
  slot: string;
  className?: string;
  aspectRatio?: string;
}) {
  useEffect(() => {
    if (typeof window === "undefined" || !banners || banners.length === 0) return;
    const sessionKey = "rl_ad_impressions_logged_v2";
    let logged = new Set<string>();
    try {
      logged = new Set(JSON.parse(sessionStorage.getItem(sessionKey) || "[]"));
    } catch {}
    for (const b of banners) {
      const dedupeKey = `${b.id}:${slot}`;
      if (logged.has(dedupeKey)) continue;
      trackAdEventAsync({
        event_type: "impression",
        asset_id: b.asset.id,
        placement_slot: slot,
        event_id: eventId,
      }).then((ok) => {
        if (!ok) return;
        try {
          const current = new Set(JSON.parse(sessionStorage.getItem(sessionKey) || "[]"));
          current.add(dedupeKey);
          sessionStorage.setItem(sessionKey, JSON.stringify([...current]));
        } catch {}
      });
    }
  }, [banners, eventId, slot]);

  if (!banners || banners.length === 0) return null;

  const handleClick = (banner: any, e: React.MouseEvent) => {
    if (!banner.asset.click_url) {
      e.preventDefault();
      return;
    }
    trackAdEvent({
      event_type: "click",
      asset_id: banner.asset.id,
      placement_slot: slot,
      event_id: eventId,
      target_url: banner.asset.click_url,
    });
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {banners.map((b) => (
        <a
          key={b.id}
          href={b.asset.click_url || "#"}
          target={b.asset.click_url ? "_blank" : undefined}
          rel="noopener noreferrer"
          onClick={(e) => handleClick(b, e)}
          className={`block ${aspectRatio} bg-surface-sunken rounded-token-lg overflow-hidden hover:opacity-95 transition-opacity flex items-center justify-center ${b.asset.click_url ? "cursor-pointer" : "cursor-default"}`}
          data-ad-placement-id={b.id}
          data-ad-asset-id={b.asset.id}
          data-ad-slot={slot}
        >
          <img
            src={b.asset.image_url}
            alt={b.asset.alt_text || b.asset.name}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        </a>
      ))}
    </div>
  );
}
